import * as moment from 'moment';
import * as psl from 'psl';
import * as React from 'react';
import { FormattedMessage } from 'react-intl';
import { connect } from "react-redux";
import { UncontrolledTooltip } from "reactstrap";
import { bindActionCreators, Dispatch } from "redux";
import { signOut as signOutAction } from "../../actions/loginActions";
import { clearMailData as clearMailDataAction, markMessageRead as markMessageReadAction } from '../../actions/mailActions';
import { addNotification as addNotificationAction, clearNotifications as clearNotificationsAction } from "../../actions/notificationsActions";
import { MailService } from "../../services/mail";
import Notification from '../common/Notification';
import UserSettings from '../settings/model/Settings';
import Message from './model/Message';
import MessageDetails from './model/MessageDetails';
import * as MessageUtil from './util/message.util';
import { sanitizeHtml } from './util/sanitize';

interface IMessagePreviewProps {
  csrf: string;
  loginTime: number;
  selectedMessage: Message;
  settings: UserSettings;
  user: string;
  wsid: string;
  addNotification: (notification: Notification) => any;
  clearMailData: () => any;
  clearNotifications: () => any;
  markMessageRead: (messageId: string) => any;
  signOut: () => any;
}

interface IMessagePreviewState {
  htmlBody: string;
  attachments: string[];
  showNoImagesBanner: boolean;
}

export class MessagePreview extends React.Component<IMessagePreviewProps, IMessagePreviewState> {
  public mailService: MailService;

  constructor(props: IMessagePreviewProps) {
    super(props);
    this.mailService = new MailService(this.props.user, this.props.wsid, this.props.csrf);
    this.state = { htmlBody: '', attachments: [], showNoImagesBanner: false };
    if (this.props.selectedMessage.id) {
      this.getMessageDetails(this.props.selectedMessage.id);
    }
  }

  public componentWillReceiveProps(nextProps: IMessagePreviewProps) {
    if (this.props.selectedMessage.id !== nextProps.selectedMessage.id) {
      this.getMessageDetails(nextProps.selectedMessage.id);
    }
  }

  public async getMessageDetails(messageId: string) {
    try {
      const response = await this.mailService.getDetails(messageId);

      if (!response.ok) {
        throw new Error(response.status.toString());
      }

      const details = await response.json() as MessageDetails;
      const body = details.content.body;
      const plainBody = body.plain ? Buffer.from(body.plain.content, 'base64').toString() : "";
      const htmlBody = body.html ? Buffer.from(body.html.content, 'base64').toString() : plainBody;
      const sanitizedHtml = sanitizeHtml(htmlBody);
      this.setState({
        attachments: Object.keys(details.content.attachments),
        htmlBody: sanitizedHtml,
        showNoImagesBanner: htmlBody.indexOf('<img') >= 0
      });
      this.props.markMessageRead(messageId);
    } catch (e) {
      if (e.message === '401') {
        if (moment().diff(this.props.loginTime, 'hours') < 12) {
          this.props.addNotification({ message: "Session Timeout", isError: true });
        }
        this.props.signOut();
        this.props.clearMailData();
      } else {
        this.props.addNotification({ messageKey: "L.Error.Retrieve", isError: true });
      }
    }
  }

  public render() {
    const message = this.props.selectedMessage;
    const htmlBody = { __html: this.state.htmlBody };
    const dateFormat = this.props.settings.dateFormat ? this.props.settings.dateFormat.toUpperCase() + ' hh:mm A' : 'MM/DD/YYYY hh:mm A';
    const unmatchingRootDomains = message.headers ? MessageUtil.hasUnmatchingDomains(message.headers.from, message.headers["return-path"]) : false;
    const returnPathDomain = message.headers && message.headers["return-path"] && message.headers["return-path"].indexOf('@') >= 0 ? message.headers["return-path"].toLowerCase().split('@')[1].replace('>', '').trim() : undefined;
    const returnPathRootDomain = returnPathDomain ? psl.get(returnPathDomain) : "Unknown";
    const attachments = this.state.attachments.map((fileName) => {
      return (
        <span className="attachment-file" key={fileName}>
          <i className="material-icons attachment">
            attach_file
        </i>{fileName}
        </span>
      )
    });

    if (!message.headers) {
      return <div>No message selected</div>;
    }

    let returnPath = message.headers['return-path'] || "";
    returnPath = returnPath.replace(/<|>/g,'')

    return (
      <div className="main">
        <div className="container-fluid">
          <div className="email-body">
            <div className="row">
              <div className="col-12">
                <h3>{message.headers.subject}</h3>
              </div>
            </div>
            <div className="row justify-content-between align-items-center">
              <div className="col-6">
                <h6>{message.headers.from || returnPath}</h6>
              </div>
              <div className="col-6 text-right">
                <span>{moment(message.headers.date).format(dateFormat)}</span>
              </div>
            </div>
            {unmatchingRootDomains && returnPathDomain &&
              <div className="row" id="unmatchingDomains">
                <div className="col-12">
                  <a className="sent-from-link" id="sent-from">
                    <FormattedMessage id="L.SentFrom" defaultMessage="sent from" />
                  </a>
                  <span title={returnPath}>{returnPathRootDomain}</span>
                  <UncontrolledTooltip className="sentFromTooltip" placement="right" target="sent-from">
                    <FormattedMessage id="L.SentFrom.Message"
                      defaultMessage="Sent from appears when the domain of the sender does not match the domain used to send the message." />
                  </UncontrolledTooltip>
                </div>
              </div>
            }
            <div className="row">
              <div className="col-10">
                <span>
                  <FormattedMessage id="L.To" defaultMessage="to" /> {message.headers.to || message.headers['x-orig-to']}
                </span>
              </div>
              {message.hasAttachment &&
                <div className="to-attach col-2 text-right">
                  <i className="material-icons attachment">attach_file</i>
                </div>
              }
            </div>

            {this.state.showNoImagesBanner && <div className="alert alert-primary alert-dismissible fade show row align-items-center justify-content-between" role="alert" id="noImageBanner">
              <span>
                <i className="material-icons">info</i>
                <FormattedMessage id="L.ImagesNotDisplayed" defaultMessage="Images Not Displayed" />
              </span>
            </div>}

            <div className="row text-body">
              <div className="col-12">
                <div dangerouslySetInnerHTML={htmlBody} />
              </div>
            </div>
            {message.hasAttachment &&
              <div className="row">
                <div className="col-12 attach-header">
                  <h6><FormattedMessage id="L.Attachments" defaultMessage="Attachments" /></h6>
                </div>
              </div>}
            {message.hasAttachment && <div className="row" id="attachmentList">
              <div className="col-12">
                {attachments}
              </div>
            </div>}
          </div>
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state: any) => {
  return {
    csrf: state.authentication.csrf,
    loginTime: state.authentication.loginTime,
    selectedMessage: state.mail.selectedMessage,
    settings: state.settings.settings,
    user: state.authentication.user,
    wsid: state.authentication.wsid,
  };
}

const mapDispatchToProps = (dispatch: Dispatch) => {
  return {
    addNotification: bindActionCreators(addNotificationAction, dispatch),
    clearMailData: bindActionCreators(clearMailDataAction, dispatch),
    clearNotifications: bindActionCreators(clearNotificationsAction, dispatch),
    markMessageRead: bindActionCreators(markMessageReadAction, dispatch),
    signOut: bindActionCreators(signOutAction, dispatch)
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(MessagePreview);



// WEBPACK FOOTER //
// ./src/components/mail/MessagePreview.tsx